import type { BadgeVariant, PositionDataType } from "@/components/atoms";
import type { ReviewType } from "@/components/molecules";
import type { BrandFormDetailsType } from "@/components/units";
import type { DishFormType } from "@/components/units/BrandFormMenu/types";
import type { Branch } from "@/store/BranchStore";

export type ProfileSettingsTableType = {
  id: string;
  name: string;
  role: string;
  level: string;
};

export type DataReservationsType = {
  id: string;
  status: { title: string; stage: BadgeVariant };
  reservationNumber: string;
  dateAndTime: string;
  user?: string;
  contacts?: string;
  branches?: string;
  guests: string;
  time?: string;
};

export type SystemFeaturedBrandTableMockType = {
  id: string;
  brand: string;
  country: string;
  status: { title: string; stage: BadgeVariant };
  featured: boolean;
};

export type RestaurantTransactionsMockType = {
  id: string;
  dateTime: string;
  orderId: string;
  amount: string;
  paymentMethod: string;
  status: { title: string; stage: BadgeVariant };
};

export type ReservationsCalendarType = {
  id: string;
  status: { title: string; stage: BadgeVariant };
  reservationNumber: string;
  user: string;
  contacts: string;
  guests: string;
  time: string;
};

export const mockDefaultMenu = { cuisine: [], categories: [] };

export const resultTableMock = 200;

export const mockBoost = {
  score: "0.73",
  overrideScore: "0.95",
  cashback: 20,
  automatic: false,
};

export const positions: PositionDataType[] = [
  {
    title: "Credit balance",
    data: "5,020",
    extra: "(out of $20k)",
    isMoney: true,
  },
  { title: "Transactions", data: "1,233", isMoney: true },
  { title: "Restaurant page visits", data: "2,526", isMoney: false },
  { title: "Active reservations online", data: "58", isMoney: false },
];

export const defaultFormDetails = {
  brandId: "123456",
  referenceNumber: "123456",
  branchNameEnglish: "Starbucks Riyadh",
  branchNameArabic: "Starbucks Riyadh",
  descriptionArabic: "",
  descriptionEnglish:
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.",
  address: "Lorem ipsum street 14, Saudi Arabia",
  phoneCode: "+966",
  phoneNumber: "1231231234",
};

export const hoursFormMocks = {
  schedules: [
    {
      day: "sunday",
      closed: false,
      open: "09:00",
      close: "23:00",
    },
    {
      day: "monday",
      closed: false,
      open: "09:00",
      close: "23:00",
    },
    {
      day: "tuesday",
      closed: false,
      open: "09:00",
      close: "23:00",
    },
    {
      day: "wednesday",
      closed: false,
      open: "09:00",
      close: "23:00",
    },
    {
      day: "thursday",
      closed: false,
      open: "09:00",
      close: "23:00",
    },
    {
      day: "friday",
      closed: true,
      open: "09:00",
      close: "23:00",
    },
    {
      day: "saturday",
      closed: true,
      open: "09:00",
      close: "23:00",
    },
  ],
};

export const reservationsFormBranch = {
  methods: ["Phone", "App"],
  time: ["<10 min"],
  reservation: true,
};

export const mediaFormBranch = {
  logotype: [],
  media: [],
};

export const mockEntireBranch: Branch = {
  details: defaultFormDetails,
  businessHours: hoursFormMocks,
  reservations: reservationsFormBranch,
  media: mediaFormBranch,
};

export const branchesUnderBrandMock = [
  { brand: "Starbucks Kuwait" },
  {
    brand: "Starbucks Riyadh",
  },
];

export const defaultVibes = {
  tags: ["Local gem"],
  ambiance: ["Casual"],
  dressCode: ["Formal"],
  priceLevel: ["2"],
};

export const defaultContacts = {
  email: "john.smith123@gmail.com",
  websiteUrl: "",
  socialLinks: [{ profile: "X", handle: "" }],
  connectedAccounts: [],
};

export const defaultAmenities = {
  amenities: ["Live music"],
  parking: ["Valet"],
};

export const defaultDetails: BrandFormDetailsType = {
  nameEn: "Starbucks",
  nameAr: "-",
  groupId: "1",
  googleBusinessId: "123456789012345678",
  commercialRegistration: "123456789",
  VAT: "852741",
  descriptionEn:
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.",
  descriptionAr:
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.",
  country: "Saudi Arabia",
  currency: "SAR",
  currencyName: "Saudi Riyal",
};

export const phoneListMock = [
  { name: "+966", id: "+966" },
  { name: "+380", id: "+380" },
];

export const languageMock = [
  { name: "English", id: "english" },
  { name: "Arabic", id: "arabic" },
];

export const priceRangeList = ["1", "2", "3", "4"].map(level =>
  "$".repeat(Number(level))
);

export const dressCodeList = [
  { id: "casual", name: "Casual" },
  { id: "smart-casual", name: "Smart Casual" },
  { id: "business-casual", name: "Business Casual" },
  { id: "formal", name: "Formal" },
  { id: "no-dress-code", name: "No Dress Code" },
];

export const accountMock = {
  name: "Blue Lotus Food",
  link: "@bluelotusfood",
  accountType: "Business",
  image: null,
};

export async function mockInstagramConnect(): Promise<{
  success: boolean;
  token?: string;
  error?: string;
}> {
  return new Promise(resolve => {
    setTimeout(() => {
      const isSuccess = Math.random() > 0.3;
      if (isSuccess) {
        resolve({ success: true, token: "mocked_instagram_token_12345" });
      } else {
        resolve({
          success: false,
          error: "Could not connect to Instagram. Please try again.",
        });
      }
    }, 1000);
  });
}

export const ambianceList = [
  "Romantic",
  "Casual",
  "Family friendly",
  "Business",
  "Elegant",
  "Cozy",
  "Trendy",
  "Outdoor",
  "Historic",
  "Quiet",
  "Lively",
  "Scenic View",
  "Rooftop",
];

export const tagsList = [
  "Fine dining",
  "Casual dining",
  "Quick service",
  "Budget friendly",
  "Family favorite",
  "Local gem",
  "Instagrammable",
  "Business hangout",
];

export const mockNotification = [
  {
    time: "1 hour ago",
    message: "Your call has been confirmed.",
  },
  {
    time: "1 hour ago",
    message: "You have a new message!",
  },
  {
    time: "2 hours ago",
    message: "Your subscription is expiring soon!",
  },
];

export const NutritionFields: { label: string; name: keyof DishFormType }[] = [
  { label: "Protein (g)", name: "protein" },
  { label: "Fiber (g)", name: "fiber" },
  { label: "Total fat (g)", name: "totalFat" },
  { label: "Total sugars (g)", name: "totalSugar" },
  { label: "Saturated fat (g)", name: "saturatedFat" },
  { label: "Added sugars (g)", name: "addedSugars" },
  { label: "Trans fat (g)", name: "transFat" },
  { label: "Salt (g)", name: "salt" },
  { label: "Cholesterol (mg)", name: "cholesterol" },
  { label: "Sodium (mg)", name: "sodium" },
  { label: "Carbohydrate (g)", name: "carbohydrate" },
  { label: "Caffeine (mg)", name: "caffeine" },
];

export const Tastes = {
  Spice: 0,
  Sweetness: 0,
  Sourness: 0,
  Bitterness: 0,
  Umami: 0,
};

export const flagsList = [
  { id: "chefs-recommendation", name: "Chef's Recommendation" },
  { id: "new-item", name: "New Item" },
  { id: "promotion", name: "Promotion (special offer)" },
];

export const allergensList = [
  { id: "nuts", name: "Nuts" },
  { id: "dairy", name: "Dairy" },
  { id: "fish", name: "Fish" },
  { id: "wheat", name: "Wheat" },
  { id: "egg", name: "Egg" },
  { id: "sesame", name: "Sesame" },
  { id: "soya", name: "Soya" },
  { id: "peanuts", name: "Peanuts" },
  { id: "shell-fish", name: "Shell fish" },
  { id: "celery", name: "Celery" },
  { id: "mustard", name: "Mustard" },
  { id: "molluscs", name: "Molluscs" },
  { id: "sulphites", name: "Sulphites" },
  { id: "lupinus", name: "Lupinus" },
];

export const allergic = {
  title: "Allergens",
  list: allergensList,
  name: "allergens",
};

export const dietList = [
  { id: "dairy-free", name: "Dairy free" },
  { id: "vegan", name: "Vegan" },
  { id: "vegetarian", name: "Vegetarian" },
  { id: "gluten-free", name: "Gluten free" },
];

export const diet = { title: "Diet", list: dietList, name: "diet" };

export const mealTimeList = [
  { id: "breakfast", name: "Breakfast" },
  { id: "lunch", name: "Lunch" },
  { id: "dinner", name: "Dinner" },
  { id: "snack", name: "Snack" },
  { id: "late-night", name: "Late night" },
];

export const mealTime = {
  title: "Meal time",
  list: mealTimeList,
  name: "meal",
};

export const occasionList = [
  { id: "romantic", name: "Romantic" },
  { id: "business", name: "Business" },
  { id: "family", name: "Family" },
  { id: "celebration", name: "Celebration" },
  { id: "casual", name: "Casual" },
  { id: "quick-bite", name: "Quick bite" },
];

export const occasion = {
  title: "Occasion",
  list: occasionList,
  name: "occasion",
};

export type ReservationStats = {
  active: { value: string; yesterday: string; lastMonth: string };
  completed: { value: string; yesterday: string; lastMonth: string };
  canceled: { value: string; yesterday: string; lastMonth: string };
  guests: { value: string; yesterday: string; lastMonth: string };
};

export const reservationStatsMock: ReservationStats = {
  active: { value: "18", yesterday: "+15.2%", lastMonth: "+8.4%" },
  completed: { value: "2", yesterday: "+15.2%", lastMonth: "+8.4%" },
  canceled: { value: "2", yesterday: "+15.2%", lastMonth: "+8.4%" },
  guests: { value: "164", yesterday: "+15.2%", lastMonth: "-2.7%" },
};

export const visibilityRulesOptionsMocks = [
  {
    name: "Has a menu with at least one dish",
    id: "Has a menu with at least one dish",
  },
  {
    name: "Has a branch at least in one city",
    id: "Has a branch at least in one city",
  },
  { name: "It is active", id: "It is active" },
  { name: "Lorem ipsum dolor sit amet", id: "Lorem ipsum dolor sit amet" },
  { name: "Lorem ipsum", id: "Lorem ipsum" },
];

export const reviewsListMocks: ReviewType[] = [
  {
    id: "543434",
    name: "Stabucks Kuwait",
    orderNumber: "#1234567",
    dateOfComment: "2025-01-20, 16:46",
    rating: "3",
    status: { title: "Neutral", value: "inactive" },
    author: "John Doe",
    textOfComment:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    isReply: true,
    dateOfReply: "2025-01-20, 16:46",
    textOfReply:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  },
  {
    id: "541434",
    name: "Manami Grill",
    orderNumber: "#1234567",
    dateOfComment: "2025-01-20, 16:46",
    rating: "5",
    status: { title: "Positive", value: "success" },
    textOfComment:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    isReply: false,
    author: "John Doe",
  },
  {
    id: "5414434",
    name: "Manami Grill",
    orderNumber: "#1234567",
    dateOfComment: "2025-01-20, 16:46",
    rating: "3",
    status: { title: "Negative", value: "warning" },
    textOfComment:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    isReply: true,
    dateOfReply: "2025-01-20, 16:46",
    author: "John Doe",
    textOfReply:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  },
  {
    id: "5414344",
    name: "Stabucks Kuwait",
    orderNumber: "#1234567",
    dateOfComment: "2025-01-20, 16:46",
    rating: "3",
    status: { title: "Neutral", value: "inactive" },
    author: "John Doe",
    textOfComment:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.",
    isReply: false,
  },
  {
    id: "5414343",
    name: "Stabucks Kuwait",
    orderNumber: "#1234567",
    dateOfComment: "2025-01-20, 16:46",
    rating: "4",
    status: { title: "Positive", value: "success" },
    author: "John Doe",
    textOfComment:
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    isReply: false,
  },
];

export const exporCSVtDataMock = [
  ["Reservation ID", "Customer", "Date", "Time", "Guests"],
  ["R001", "Alice", "2025-10-23", "18:00", "4"],
  ["R002", "Bob", "2025-10-24", "19:30", "2"],
  ["R003", "Charlie", "2025-10-25", "20:00", "6"],
];

export const emailMock = "yousef@spice.com";

export const roleOptions = [
  { name: "Waiter", id: "waiter" },
  { name: "Admin", id: "admin" },
];

export const profileSettingsMockData: ProfileSettingsTableType[] = [
  { id: "2332234", level: "Brand", name: "Starbucks", role: "Admin" },
  { id: "23322374", level: "Branch", name: "Starbucks Kuwait", role: "Waiter" },
  { id: "23332234", level: "Branch", name: "Starbucks Riyadh", role: "Waiter" },
  { id: "23342234", level: "Group", name: "John Smith & Co", role: "Admin" },
];

export const initialRoles = Object.fromEntries(
  profileSettingsMockData.map(({ id, role }) => [id, role.toLowerCase()])
);

export const reviewStatsMock = {
  summary: {
    totalReviews: {
      value: "32",
      change: "-20.1%",
    },
    averageRating: {
      value: "4.6/5",
      change: "+20.1%",
    },
  },
  breakdown: [
    {
      label: "Positive reviews",
      value: "18",
      change: "+20.1%",
      keywords: [
        "Location",
        "Staff",
        "Easy payment",
        "Security",
        "Customer service",
        "Cleanness",
        "Vegetarian menu",
        "Interior",
        "Working on weekends",
      ],
    },
    {
      label: "Neutral reviews",
      value: "12",
      change: "-2.2%",
      keywords: ["Location", "Staff", "Easy payment", "Security", "Accessible"],
    },
    {
      label: "Negative reviews",
      value: "2",
      change: "+15.2%",
      keywords: [
        "Staff",
        "Coffee",
        "Security",
        "Payments",
        "Terrible music",
        "No napkins",
      ],
    },
  ],
};

export const quickSummary = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.",
];

export const dataReservationsBrandMock: DataReservationsType[] = [
  {
    id: "3423423234",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    dateAndTime: "2025-04-15, 17:00",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    branches: "Starbucks Kuwait",
    guests: "7",
  },
  {
    id: "3423423234",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    dateAndTime: "2025-04-15, 17:00",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    branches: "Starbucks Kuwait",
    guests: "7",
  },
  {
    id: "3423423234",
    status: { title: "Canceled", stage: "warning" },
    reservationNumber: "#90123456",
    dateAndTime: "2025-04-15, 17:00",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    branches: "Starbucks Kuwait",
    guests: "7",
  },
  {
    id: "3423423234",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    dateAndTime: "2025-04-15, 17:00",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    branches: "Starbucks Kuwait",
    guests: "7",
  },
  {
    id: "3423423234",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    dateAndTime: "2025-04-15, 17:00",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    branches: "Starbucks Kuwait",
    guests: "7",
  },
  {
    id: "3423423234",
    status: { title: "Canceled", stage: "warning" },
    reservationNumber: "#90123456",
    dateAndTime: "2025-04-15, 17:00",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    branches: "Starbucks Kuwait",
    guests: "7",
  },
  {
    id: "3423423234",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    dateAndTime: "2025-04-15, 17:00",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    branches: "Starbucks Kuwait",
    guests: "7",
  },
];

export const reservationsWithoutBranches: DataReservationsType[] =
  dataReservationsBrandMock.map(({ branches, ...rest }) => rest);

export const reservationsWithoutContactsAndUser: DataReservationsType[] =
  dataReservationsBrandMock.map(({ contacts, user, ...rest }) => rest);

export const systemFeaturedBrandTableMock: SystemFeaturedBrandTableMockType[] =
  [
    {
      id: "34234234",
      brand: "McDonald’s",
      country: "Saudi Arabia",
      status: { title: "Active", stage: "success" },
      featured: true,
    },
    {
      id: "34277234",
      brand: "Burger King",
      country: "Kuwait",
      status: { title: "Active", stage: "success" },
      featured: true,
    },
    {
      id: "34234544",
      brand: "Wendy's",
      country: "Saudi Arabia",
      status: { title: "Active", stage: "success" },
      featured: true,
    },
    {
      id: "36334234",
      brand: "Subway",
      country: "Kuwait",
      status: { title: "Active", stage: "success" },
      featured: true,
    },
    {
      id: "34234268",
      brand: "KFC",
      country: "Saudi Arabia",
      status: { title: "Inactive", stage: "default" },
      featured: true,
    },
    {
      id: "34455434",
      brand: "Taco Bell",
      country: "Kuwait",
      status: { title: "Active", stage: "success" },
      featured: true,
    },
    {
      id: "34277774",
      brand: "Pizza Hut",
      country: "Saudi Arabia",
      status: { title: "Active", stage: "success" },
      featured: true,
    },
  ];

export const restaurantTransactionsDataMock: RestaurantTransactionsMockType[] =
  [
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "Card",
      status: { title: "Settled", stage: "success" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "Bank transfer",
      status: { title: "Pending", stage: "info" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "-",
      status: { title: "Failed", stage: "warning" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "Card",
      status: { title: "Settled", stage: "success" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "Bank transfer",
      status: { title: "Pending", stage: "info" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "-",
      status: { title: "Failed", stage: "warning" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "Card",
      status: { title: "Settled", stage: "success" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "Bank transfer",
      status: { title: "Pending", stage: "info" },
    },
    {
      id: "32324234234",
      dateTime: "2025-03-18, 10:30",
      orderId: "#123456",
      amount: "$135.00",
      paymentMethod: "-",
      status: { title: "Failed", stage: "warning" },
    },
  ];

export const dataReservationsForCalendar: ReservationsCalendarType[] = [
  {
    id: "1",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "2",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "3",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "4",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "5",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "6",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "7",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "8",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "9",
    status: { title: "Canceled", stage: "warning" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "09:00",
  },
  {
    id: "10",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "11",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "08:00",
  },
  {
    id: "12",
    status: { title: "Canceled", stage: "warning" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "11:30",
  },
  {
    id: "13",
    status: { title: "Canceled", stage: "warning" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "11:30",
  },
  {
    id: "14",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "15",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "16",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "17",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "18",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "19",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "20",
    status: { title: "Completed", stage: "default" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "21",
    status: { title: "Pending", stage: "info" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "13:30",
  },
  {
    id: "22",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "10:30",
  },
  {
    id: "23",
    status: { title: "Pending", stage: "info" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "10:30",
  },
  {
    id: "24",
    status: { title: "Pending", stage: "info" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "10:30",
  },
  {
    id: "25",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "16:30",
  },
  {
    id: "26",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "16:30",
  },
  {
    id: "27",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "16:30",
  },
  {
    id: "28",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "16:30",
  },
  {
    id: "29",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "16:30",
  },
  {
    id: "30",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "16:30",
  },
  {
    id: "31",
    status: { title: "Active", stage: "success" },
    reservationNumber: "#90123456",
    user: "Ed Truman",
    contacts: "lorem.ipsum@email.com +966 000 000 0000",
    guests: "7",
    time: "15:00",
  },
];

export const paymentMethodListMock = ["Card", "Bank transfer"];

export const nameCustomersListMocks = [
  "Jeffrey Bloxam",
  "Jillie Sanders",
  "John Doe",
];

export const countryListMock = ["Kuwait", "Saudi Arabia"];

export const brandListMocks = [
  "McDonald’s",
  "Blue Lotus Food",
  "Starbucks",
  "Food",
];

export const branchListMock = [
  "Molly’s Kitchen",
  "Smith & Sons",
  "Culinary Paradise",
];
